import apiClient from "../api/client";

import { home } from "../constants/actionTypes";
// eslint-disable-next-line import/no-cycle
import { actions as mainActions } from "./main";
import { resolveListingApiName } from "../constants/listings";
import { buildListingTeamSearch, resolveListingTeamContext } from "../helpers/listingTeamContext";
import { buildListingCreatedParams, REUSED_DRAFT_MESSAGE } from "../helpers/listingCreatedParams";
import { track, trackDataLayer } from "../helpers/track";
import { eventName, eventNameDataLayer } from "../constants/track";

export const actions = {
  getDashboardInfo() {
    return dispatch => {
      dispatch(this.setLoading("dashboard", true));

      apiClient
        .get("/members/json-api/index/home")
        .then(res => {
          dispatch({
            type: home.GET_DASHBOARD_DATA_SUCCESS,
            payload: res.data
          });
        })
        .catch(err =>
          dispatch({
            type: home.GET_DASHBOARD_DATA_FAIL,
            payload: err
          })
        );
    };
  },

  getAddNewListingData(isModal) {
    return (dispatch, getState) => {
      const {
        home: { addNewListingData, preloaders }
      } = getState();

      if (Object.keys(addNewListingData || {}).length || preloaders.addNewListingData) {
        return Promise.resolve();
      }

      dispatch(this.setLoading("addNewListingData", true));
      apiClient({ method: "get", responseType: "json", url: "/members/json-api/index/lists" })
        .then(response => {
          const priority = isModal ? ["Agents", "Doctors", "Lawyers"] : ["Accountants", "Agents", "Doctors", "Lawyers"];
          const professionsSort = response.data.professions
            .filter(item => !item.title.includes("Profile"))
            .sort((a, b) => (priority.includes(a.title) ? (priority.includes(b.title) ? (a.title < b.title ? -1 : 0) : -1) : 1));

          dispatch({
            type: home.GET_ADD_NEW_LISTING_DATA,
            payload: {
              ...response?.data,
              professions: professionsSort
            }
          });
        })
        .catch(() => {
          dispatch({
            type: home.GET_ADD_NEW_LISTING_DATA_FAIL
          });
        });

      return Promise.resolve();
    };
  },

  addNewListing(data, pathname, listingCreatePage, listingType, navigate) {
    return (dispatch, getState) => {
      const {
        account: { profile }
      } = getState();

      dispatch(this.setLoading("listingAssistant", true));

      const isEvensOrJobsPage =
        ["/my-events", "/my-jobs", "/my-organizations"].some(item => pathname.includes(item)) ||
        ["Events", "Jobs", "Organizations"].some(item => listingType.includes(item));
      const listingName = resolveListingApiName(pathname) || String(listingType || "").toLowerCase();
      const { apiPrefix, userIdParams } = resolveListingTeamContext(getState, pathname);

      const url = isEvensOrJobsPage && listingName ? `${apiPrefix}/${listingName}` : "/members/json-api/signup/copy-or-create-listing";

      apiClient({
        method: "post",
        responseType: "json",
        url,
        data: isEvensOrJobsPage ? { ...data, ...userIdParams } : data,
        headers: { "Content-Type": "application/json" }
      })
        .then(res => {
          const responseData = res?.data || {};
          const listingId = isEvensOrJobsPage ? responseData?.data?.listing_id : responseData?.listing_id;

          if (responseData?.success === false || !listingId) {
            dispatch({
              type: home.ADD_NEW_LISTING_FAIL,
              payload: responseData
            });
            dispatch(mainActions.toggleNewListing(false));
            return;
          }

          dispatch({
            type: home.ADD_NEW_LISTING,
            payload: responseData
          });

          dispatch(mainActions.toggleNewListing(false));

          const listPathname = isEvensOrJobsPage ? `/my-${listingName}` : "/listings";
          const teamSearch = buildListingTeamSearch(getState, pathname);

          navigate(`${listPathname}/${listingId}${teamSearch}`);

          track(eventName.addToCart, { em: profile.email, fn: profile.name_f, ln: profile.name_l });

          if (responseData?.message !== REUSED_DRAFT_MESSAGE) {
            trackDataLayer(
              eventNameDataLayer.listingCreated,
              buildListingCreatedParams({
                // Only the api-v2 route has a listing type: a profile create's pathname can still name one (/public-organizations).
                listingName: isEvensOrJobsPage ? listingName : "",
                listing: isEvensOrJobsPage ? responseData?.data : responseData,
                directories: getState().home?.addNewListingData?.directories,
                profession: listingType,
                createPage: listingCreatePage
              })
            );
          }
        })
        .catch(() => {
          dispatch({
            type: home.ADD_NEW_LISTING_FAIL
          });
          dispatch(mainActions.toggleNewListing(false));
        })
        .finally(() => {
          dispatch(this.setLoading("listingAssistant", false));
        });
    };
  },

  getSaved(command = "latest") {
    return dispatch => {
      dispatch(this.setLoading("saved", true));

      apiClient({ method: "get", url: `/members/api-v2/bookmarks?command=${command}&expand=lawfirms,practices,parent_company` })
        .then(response => {
          const { records } = response.data.data;
          let payload;
          if (command === "latest") {
            payload = records;
          } else {
            // Initialize all keys to empty arrays
            payload = {
              profiles: [],
              organizations: [],
              events: [],
              jobs: []
            };
            // Only update the key relevant to the command
            payload[command] = records;
          }

          dispatch({
            type: home.GET_SAVED,
            payload: {
              list: payload,
              count: response.data.data.counting
            }
          });
        })
        .catch(() => {
          dispatch({
            type: home.GET_SAVED_FAIL
          });
        });
    };
  },

  /**
   * @param {number|null} [fresh] time of the last change on /following; a new value changes the URL so the
   *   browser does not reuse the feed's 60-second cached response (GROWTH-7042)
   */
  getMyFeed(command = "all", search, page, limit, fresh) {
    return dispatch => {
      dispatch(this.setLoading("feed", true));

      return apiClient({
        method: "get",
        url: `/members/api-v2/my-feed?command=${command}&expand=lawfirms,practices,parent_company,event_tickets,text_lang${
          page ? `&page=${page}` : ""
        }${limit ? `&limit=${limit}` : ""}${search ? `&filters[0][field]=name&filters[0][value]=${encodeURIComponent(search)}` : ""}${
          fresh ? `&fresh=${fresh}` : ""
        }`
      })
        .then(response => {
          dispatch({
            type: home.GET_MY_FEED,
            payload: {
              list: response.data.data.records,
              count: response.data.data.counting,
              pagination: response.data.pagination,
              command
            }
          });
        })
        .catch(() => {
          dispatch({
            type: home.GET_MY_FEED_FAIL
          });
        });
    };
  },

  getFollowing() {
    return dispatch => {
      dispatch(this.setLoading("feed", true));

      apiClient({ method: "get", url: "/members/api-v2/user-following" })
        .then(response => {
          dispatch({
            type: home.GET_FOLLOWING,
            payload: response.data.data
          });
        })
        .catch(() => {
          dispatch({
            type: home.GET_FOLLOWING_FAIL
          });
        });
    };
  },

  removeFollowing(id) {
    return dispatch => {
      dispatch(this.setLoading("removeFollowing", true));

      return apiClient({ method: "DELETE", url: `/members/api-v2/user-following?id=${id}` })
        .then(() => {
          dispatch({
            type: home.REMOVE_FOLLOWING,
            payload: id
          });
          return true;
        })
        .catch(err => {
          dispatch({
            type: home.REMOVE_FOLLOWING_FAIL
          });
          return Promise.reject(err);
        });
    };
  },

  /**
   * @param {number} listingId
   * @param {function(): number} [now] clock for the change time FeedStream sends as `fresh`
   */
  addToFavorite(listingId, now = Date.now) {
    return dispatch => {
      dispatch(this.setLoading("page", true));

      return apiClient({
        method: "post",
        responseType: "json",
        url: "/members/api-v2/bookmarks",
        params: {
          content_id: listingId,
          type: "listing"
        }
      })
        .then(res => {
          dispatch({
            type: home.ADD_FAVORITES,
            payload: listingId,
            meta: { changedAt: now() }
          });

          return res.data.success;
        })
        .catch(error => {
          dispatch({
            type: home.ADD_FAVORITES_FAIL,
            payload: error?.response?.data?.error
          });
        });
    };
  },

  /**
   * @param {number} listingId
   * @param {function(): number} [now] clock for the change time FeedStream sends as `fresh`
   */
  removeFavorite(listingId, now = Date.now) {
    return dispatch => {
      dispatch(this.setLoading("page", true));

      return apiClient({
        method: "delete",
        responseType: "json",
        url: "/members/api-v2/bookmarks",
        params: {
          content_id: listingId,
          type: "listing"
        }
      })
        .then(res => {
          dispatch({
            type: home.REMOVE_FAVORITES,
            payload: listingId,
            meta: { changedAt: now() }
          });

          return res.data.success;
        })
        .catch(error => {
          dispatch({
            type: home.REMOVE_FAVORITES_FAIL,
            payload: error?.response?.data?.error
          });
        });
    };
  },

  /**
   * GET /api-v2/feed — the unified My Feed stream (GROWTH-7127/7128). `append: true` is a
   * "Show older" click: the reducer concatenates onto the existing list instead of replacing
   * it, and loading goes on the "feedStreamMore" section so it doesn't cover the whole page.
   *
   * @param {Object} [params]
   * @param {string} [params.type] one of FeedQuery::TYPES ("profile"|"organization"|"event"|"job"|"referral")
   * @param {number} [params.communityId] the sidebar's selected community (GROWTH-7130); omitted for "All communities"
   * @param {string} [params.cursor] opaque cursor from a previous page's next_cursor
   * @param {number} [params.fresh] last follow/bookmark change time — busts the 60s browser cache
   * @param {string} [params.q] the header search's text (GROWTH-7577): only listings matching it, counts and total narrowed
   * @param {boolean} [params.append=false]
   * @param {AbortSignal} [params.signal] FeedStream aborts a load its filter change made stale
   * @returns {Function}
   */
  getFeedStream({ type, communityId, cursor, fresh, q, append = false, signal } = {}) {
    const section = append ? "feedStreamMore" : "feedStream";

    return dispatch => {
      dispatch(this.setLoading(section, true));

      return apiClient
        .get("/members/api-v2/feed", { params: { type, community_id: communityId, cursor, fresh, q }, signal })
        .then(response => {
          const {
            items,
            next_cursor: nextCursor,
            last_seen_at: lastSeenAt,
            new_count: newCount,
            new_by_community: newByCommunity,
            // GROWTH-7573: optional until the backend sends them — the sidebar's per-type counts
            // and the "N items" line above the stream are left out while they're absent.
            type_counts: typeCounts = null,
            total = null,
            // GROWTH-7860: the most the list pages, sent only when total is over it.
            list_limit: listLimit = null
          } = response.data.data;
          dispatch({
            type: home.GET_FEED_STREAM,
            payload: { items, nextCursor, lastSeenAt, newCount, newByCommunity, typeCounts, total, listLimit, append, section }
          });
        })
        .catch(error => {
          // Aborted by FeedStream: a newer load already owns the page's preloader, so only an
          // abandoned "Show older" hands its spinner back.
          if (apiClient.isCancel(error)) {
            if (append) dispatch(this.setLoading(section, false));
            return;
          }
          dispatch({
            type: home.GET_FEED_STREAM_FAIL,
            payload: {
              section,
              message: error?.response?.data?.error || "Failed to load your feed",
              status: error?.response?.status || null
            }
          });
        });
    };
  },

  /**
   * GET /api-v2/feed/upcoming-events (GROWTH-7131) — up to 8 events across the member's whole
   * feed whose next occurrence falls in the next 14 days, for the strip above the stream.
   * Not paginated (no cursor, no "Show older") — a fixed-size strip, unlike getFeedStream.
   *
   * @param {Object} [params]
   * @param {number} [params.communityId] the sidebar's selected community, if any (GROWTH-7130)
   * @param {number} [params.fresh] last follow/bookmark change time — busts the 60s browser cache
   * @param {AbortSignal} [params.signal]
   * @returns {Function}
   */
  getUpcomingEvents({ communityId, fresh, signal } = {}) {
    return dispatch => {
      dispatch(this.setLoading("upcomingEvents", true));

      return apiClient
        .get("/members/api-v2/feed/upcoming-events", { params: { community_id: communityId, fresh }, signal })
        .then(response => {
          dispatch({
            type: home.GET_UPCOMING_EVENTS,
            payload: { items: response.data.data.items }
          });
        })
        .catch(error => {
          if (apiClient.isCancel(error)) return;
          dispatch({
            type: home.GET_UPCOMING_EVENTS_FAIL,
            payload: {
              section: "upcomingEvents",
              message: error?.response?.data?.error || "Failed to load your upcoming events",
              status: error?.response?.status || null
            }
          });
        });
    };
  },

  /**
   * POST /api-v2/feed/seen (GROWTH-7128) — the page calls this once after it renders the
   * feed. Its response isn't rendered (the GET already returned the last_seen_at this render
   * uses), so there is nothing to dispatch on success; a failure is still reported, never
   * swallowed, since a silent one would quietly leave "new since" counts stuck.
   *
   * @returns {Function}
   */
  markFeedSeen() {
    return dispatch => {
      return apiClient.post("/members/api-v2/feed/seen").catch(error => {
        dispatch({
          type: home.MARK_FEED_SEEN_FAIL,
          payload: {
            section: "feedSeen",
            message: error?.response?.data?.error || "Failed to record your feed visit",
            status: error?.response?.status || null
          }
        });
      });
    };
  },

  /**
   * GET /api-v2/feed/saved (GROWTH-7140) — the Saved tab in feed-card shape. Same cursor and
   * append convention as getFeedStream; no last_seen_at/new_count, saved items are never "new".
   *
   * @param {Object} [params]
   * @param {string} [params.type]
   * @param {string} [params.cursor]
   * @param {boolean} [params.append=false]
   * @param {AbortSignal} [params.signal] FeedStream aborts a load its filter change made stale
   * @returns {Function}
   */
  getSavedFeed({ type, cursor, append = false, signal } = {}) {
    const section = append ? "savedFeedMore" : "savedFeed";

    return dispatch => {
      dispatch(this.setLoading(section, true));

      return apiClient
        .get("/members/api-v2/feed/saved", { params: { type, cursor }, signal })
        .then(response => {
          const { items, next_cursor: nextCursor } = response.data.data;
          dispatch({
            type: home.GET_SAVED_FEED,
            payload: { items, nextCursor, append, section }
          });
        })
        .catch(error => {
          if (apiClient.isCancel(error)) {
            if (append) dispatch(this.setLoading(section, false));
            return;
          }
          dispatch({
            type: home.GET_SAVED_FEED_FAIL,
            payload: {
              section,
              message: error?.response?.data?.error || "Failed to load your saved listings",
              status: error?.response?.status || null
            }
          });
        });
    };
  },

  /**
   * Drops one item from the Saved-tab list, client-side, right after a successful unsave —
   * unlike the feed stream, a saved listing that's no longer bookmarked has no reason to
   * still be on this page. No new request: the caller already got a 2xx from removeFavorite.
   */
  removeSavedFeedItem(id) {
    return {
      type: home.REMOVE_SAVED_FEED_ITEM,
      payload: id
    };
  },

  /**
   * POST /members/json-api/public/send-message (GROWTH-7132) — the same "message an attorney"
   * endpoint the public profile page's MessageModal already posts to, reused so a feed card's
   * Message CTA doesn't duplicate its backend validation/notification behaviour.
   *
   * @param {Object} data
   * @param {number} data.listingId
   * @param {number} data.dirId
   * @param {string} data.fullName
   * @param {string} data.phone
   * @param {string} data.message
   * @returns {Function} a thunk resolving to `{ ok: true }`, or `{ ok: false, message }` with the
   *   endpoint's own reason (null when it gave none) — also stored in `errors.feedMessage`
   */
  sendFeedMessage({ listingId, dirId, fullName, phone, message }) {
    return dispatch => {
      return apiClient({
        method: "post",
        responseType: "json",
        url: "/members/json-api/public/send-message",
        data: { listing_id: listingId, dir_id: dirId, name_full: fullName, phone, message, type: "listing" }
      })
        .then(res => {
          if (res.data.ok === false) {
            const reason = res.data.msg || null;
            dispatch({
              type: home.SEND_FEED_MESSAGE_FAIL,
              payload: { section: "feedMessage", message: reason, status: null }
            });
            return { ok: false, message: reason };
          }
          dispatch({ type: home.SEND_FEED_MESSAGE });
          return { ok: true };
        })
        .catch(error => {
          const reason = error?.response?.data?.error || null;
          dispatch({
            type: home.SEND_FEED_MESSAGE_FAIL,
            payload: { section: "feedMessage", message: reason, status: error?.response?.status || null }
          });
          return { ok: false, message: reason };
        });
    };
  },

  setLoading(section, isSectionLoading) {
    return {
      type: home.SET_LOADING,
      payload: { section, isSectionLoading }
    };
  },

  removeAllError() {
    return {
      type: home.REMOVE_ALL_ERROR
    };
  }
};
